var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as signalR from '@microsoft/signalr';
import { trimSlash } from '@utils';
// One connection per hub path, shared by everything on the page.
const hubs = {};
function log(level, ...args) {
    (level === 'error' ? console.error : console.info)(`[Hub : ${new Date().toISOString()}]`, ...args);
}
/**
 * Connect to a server SignalR hub, such as `/lucyhub`. The connection is shared per path and reconnects by itself.
 * Use it for hubs other than the message bus; for channels use `useMessageBus` and `publishMessage`.
 *
 * @export
 * @param context The Lucy environment context (its `lucyUrl` is the server)
 * @param path The hub path, for example `/lucyhub`
 *
 * @example
 * ```
 * const hub = await connectHub(props.uxpContext, '/lucyhub');
 * hub.on('logger', (feed, time, category, message) => console.log(message));
 * await hub.invoke('Monitor', props.uxpContext.apiKey, '/eventstream');
 * ```
 */
export function connectHub(context, path) {
    return __awaiter(this, void 0, void 0, function* () {
        const key = '/' + String(path || '').trim().replace(/^\/+/, '').toLowerCase();
        let hub = hubs[key];
        if (!hub) {
            const connection = new signalR.HubConnectionBuilder()
                .withUrl(trimSlash(context.lucyUrl) + key, { skipNegotiation: true, transport: signalR.HttpTransportType.WebSockets })
                .withAutomaticReconnect()
                .configureLogging(signalR.LogLevel.Warning)
                .build();
            hub = hubs[key] = { connection, ready: null, reconnected: new Set() };
            connection.onreconnected(() => hub.reconnected.forEach((cb) => cb()));
        }
        if (hub.connection.state === signalR.HubConnectionState.Disconnected && !hub.ready) {
            hub.ready = hub.connection.start()
                .then(() => log('info', `Connected to ${key}`))
                .catch((err) => {
                log('error', `Could not connect to ${key}:`, err);
                throw err;
            })
                .finally(() => { hub.ready = null; });
        }
        if (hub.ready)
            yield hub.ready;
        const c = hub.connection;
        return {
            on: (method, handler) => c.on(method, handler),
            off: (method, handler) => c.off(method, handler),
            invoke: (method, ...args) => c.invoke(method, ...args),
            onReconnected: (callback) => { hub.reconnected.add(callback); },
        };
    });
}
